import React, { useEffect, useRef } from 'react';

function getPixelSize(displaySize, video) {
    const dpr = window.devicePixelRatio || 1;
    const target = Math.round(displaySize * Math.max(dpr, 2));
    const vw = video?.videoWidth || 0;
    const vh = video?.videoHeight || 0;
    if (vw > 0 && vh > 0) {
        return Math.min(720, Math.min(vw, vh), Math.max(target, 64));
    }
    return Math.min(720, Math.max(target, 64));
}

function drawFrame(ctx, video, pixelSize) {
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh || !pixelSize) {
        return;
    }

    ctx.clearRect(0, 0, pixelSize, pixelSize);
    const radius = pixelSize / 2;
    ctx.save();
    ctx.beginPath();
    ctx.arc(radius, radius, radius, 0, Math.PI * 2);
    ctx.clip();

    const crop = Math.min(vw, vh);
    const sx = (vw - crop) / 2;
    const sy = (vh - crop) / 2;
    ctx.drawImage(video, sx, sy, crop, crop, 0, 0, pixelSize, pixelSize);
    ctx.restore();
}

function CircularVideoAvatar({ src, size, videoRef, onVideoReady, onVideoError }) {
    const canvasRef = useRef(null);
    const ctxRef = useRef(null);
    const pixelSizeRef = useRef(0);
    const rafRef = useRef(null);
    const readyNotifiedRef = useRef(false);
    const errorNotifiedRef = useRef(false);
    const onVideoReadyRef = useRef(onVideoReady);
    const onVideoErrorRef = useRef(onVideoError);

    onVideoReadyRef.current = onVideoReady;
    onVideoErrorRef.current = onVideoError;

    useEffect(() => {
        readyNotifiedRef.current = false;
        errorNotifiedRef.current = false;
    }, [src]);

    useEffect(() => {
        let cancelled = false;
        let detach = null;

        const setupVideo = (video, canvas) => {
            const notifyError = () => {
                if (readyNotifiedRef.current || errorNotifiedRef.current) {
                    return;
                }
                errorNotifiedRef.current = true;
                onVideoErrorRef.current?.();
            };

            const syncCanvas = () => {
                const pixelSize = getPixelSize(size, video);
                if (pixelSize !== pixelSizeRef.current) {
                    pixelSizeRef.current = pixelSize;
                    canvas.width = pixelSize;
                    canvas.height = pixelSize;
                    const ctx = canvas.getContext('2d', { alpha: true });
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';
                    ctxRef.current = ctx;
                }
                return ctxRef.current;
            };

            const paint = () => {
                const ctx = syncCanvas();
                if (!ctx || video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
                    return false;
                }
                try {
                    drawFrame(ctx, video, canvas.width);
                    return true;
                } catch (error) {
                    return false;
                }
            };

            const notifyReady = () => {
                if (readyNotifiedRef.current || errorNotifiedRef.current) {
                    return;
                }
                // Exiger une frame peinte après lecture réelle (playing),
                // pas seulement metadata — sinon faux ready / canvas vide.
                // Pas de getImageData : canvas souvent "tainted" (CORS) → bloquait Chrome.
                if (!paint()) {
                    return;
                }
                readyNotifiedRef.current = true;
                onVideoReadyRef.current?.();
            };

            const startPlayback = () => {
                try {
                    video.currentTime = 0;
                } catch (e) {}
                const playPromise = video.play();
                if (playPromise && typeof playPromise.catch === "function") {
                    playPromise.catch(() => {
                        video.muted = true;
                        video.play().catch(() => {
                            notifyError();
                        });
                    });
                }
            };

            const stopLoop = () => {
                if (rafRef.current) {
                    cancelAnimationFrame(rafRef.current);
                    rafRef.current = null;
                }
            };

            const startLoop = () => {
                stopLoop();
                const loop = () => {
                    paint();
                    if (!readyNotifiedRef.current && !video.paused && video.readyState >= 2) {
                        notifyReady();
                    }
                    if (!video.paused && !video.ended) {
                        rafRef.current = requestAnimationFrame(loop);
                    } else {
                        rafRef.current = null;
                    }
                };
                rafRef.current = requestAnimationFrame(loop);
            };

            const onPlaying = () => {
                startLoop();
                notifyReady();
            };
            const onLoadedData = () => {
                paint();
                startPlayback();
            };
            const onLoadedMetadata = () => {
                syncCanvas();
                paint();
                startPlayback();
            };
            const onError = () => {
                notifyError();
            };

            video.addEventListener('playing', onPlaying);
            video.addEventListener('loadeddata', onLoadedData);
            video.addEventListener('loadedmetadata', onLoadedMetadata);
            video.addEventListener('error', onError);

            syncCanvas();
            if (video.readyState >= 2) {
                startPlayback();
            }

            return () => {
                stopLoop();
                try {
                    video.pause();
                    video.currentTime = 0;
                } catch (e) {}
                video.removeEventListener('playing', onPlaying);
                video.removeEventListener('loadeddata', onLoadedData);
                video.removeEventListener('loadedmetadata', onLoadedMetadata);
                video.removeEventListener('error', onError);
            };
        };

        const attach = () => {
            const currentVideo = videoRef?.current;
            const currentCanvas = canvasRef.current;
            if (!currentVideo || !currentCanvas) {
                if (!cancelled) {
                    requestAnimationFrame(attach);
                }
                return;
            }
            detach = setupVideo(currentVideo, currentCanvas);
        };

        attach();

        return () => {
            cancelled = true;
            detach?.();
        };
    }, [src, size, videoRef]);

    return (
        <div
            style={{
                position: 'relative',
                width: size,
                height: size,
                flexShrink: 0,
            }}
        >
            <video
                ref={videoRef}
                src={src}
                playsInline
                muted
                preload="auto"
                loop={false}
                style={{
                    position: 'absolute',
                    width: size,
                    height: size,
                    opacity: 0,
                    pointerEvents: 'none',
                }}
            />
            <canvas
                ref={canvasRef}
                style={{
                    position: 'relative',
                    display: 'block',
                    width: size,
                    height: size,
                }}
            />
        </div>
    );
}

export default CircularVideoAvatar;
